import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Flex,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  View,
} from '@aws-amplify/ui-react';
import { callAgoGet } from '../auth/portalSession';

/**
 * Admin-only smoke / ops page for /ago → api3:* proxy and Dev ECS controls.
 */
export default function SystemsDashboard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [devLoading, setDevLoading] = useState(false);
  const [devActing, setDevActing] = useState(false);
  const [devError, setDevError] = useState('');
  const [devStatus, setDevStatus] = useState(null);

  async function runUpdateCheck() {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await callAgoGet({
        api_endpoint: 'api3:health',
        request_type: 'GET',
      });
      setResult(res);
    } catch (e) {
      const message = e?.message ? String(e.message) : 'Request failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  const refreshDevEcs = useCallback(async () => {
    setDevLoading(true);
    setDevError('');
    try {
      const res = await callAgoGet({
        api_endpoint: 'api3:admin:portal:dev_ecs',
        request_type: 'GET',
      });
      if (res?.status === 'error') {
        setDevError(res?.message || 'Failed to load Dev ECS status');
      }
      setDevStatus(res);
    } catch (e) {
      setDevError(e?.message ? String(e.message) : 'Failed to load Dev ECS status');
      setDevStatus(null);
    } finally {
      setDevLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshDevEcs();
  }, [refreshDevEcs]);

  async function setDevEcs(action) {
    const label = action === 'on' ? 'ON' : 'OFF';
    if (
      !window.confirm(
        `Set allowlisted Dev ECS services to ${label}?\n\n` +
          'This only affects *_dev services in cluster agofar (not prod).\n' +
          'Dev ALBs / Redis keep billing while ECS is off.',
      )
    ) {
      return;
    }
    setDevActing(true);
    setDevError('');
    try {
      const res = await callAgoGet({
        api_endpoint: 'api3:admin:portal:dev_ecs',
        request_type: 'POST',
        action,
      });
      setDevStatus(res);
      if (res?.status === 'error') {
        setDevError(
          res?.message ||
            (res?.errors && res.errors.join('; ')) ||
            `Failed to set Dev ECS ${label}`,
        );
      }
    } catch (e) {
      setDevError(e?.message ? String(e.message) : `Failed to set Dev ECS ${label}`);
    } finally {
      setDevActing(false);
    }
  }

  const overall = String(devStatus?.overall || 'unknown').toLowerCase();
  const overallBadge =
    overall === 'on' ? 'success' : overall === 'off' ? 'warning' : 'info';

  return (
    <View padding="1.5rem" maxWidth="52rem">
      <Heading level={3}>Systems Dashboard</Heading>
      <Text marginTop="0.5rem" color="font.secondary">
        Office admin ops. Tests the <code>/ago</code> proxy and controls allowlisted
        non-prod ECS services.
      </Text>

      <View marginTop="2rem" padding="1.25rem" backgroundColor="background.secondary"
        style={{ borderRadius: 8 }}>
        <Flex justifyContent="space-between" alignItems="center" wrap="wrap" gap="1rem">
          <View>
            <Heading level={4}>Dev ECS</Heading>
            <Text fontSize="small" color="font.secondary">
              Cluster <code>{devStatus?.cluster || 'agofar'}</code> — allowlisted{' '}
              <code>*_dev</code> only
            </Text>
          </View>
          <Badge variation={overallBadge} size="large">
            {devLoading ? '…' : overall.toUpperCase()}
          </Badge>
        </Flex>

        <Flex marginTop="1rem" gap="0.75rem" wrap="wrap">
          <Button
            variation="primary"
            onClick={() => setDevEcs('on')}
            isLoading={devActing}
            isDisabled={devLoading || devActing}
          >
            Turn on
          </Button>
          <Button
            variation="warning"
            onClick={() => setDevEcs('off')}
            isLoading={devActing}
            isDisabled={devLoading || devActing}
          >
            Turn off
          </Button>
          <Button
            variation="link"
            onClick={refreshDevEcs}
            isDisabled={devLoading || devActing}
          >
            Refresh
          </Button>
        </Flex>

        <Text marginTop="0.75rem" fontSize="small" color="font.secondary">
          Off sets desired count to 0. On restores the saved desired map. ALBs and
          Redis are not changed.
        </Text>

        {devError ? (
          <Alert variation="error" marginTop="1rem" isDismissible={false}>
            {devError}
            {devStatus?.errors?.length ? (
              <View as="pre" marginTop="0.5rem" style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                {devStatus.errors.join('\n')}
              </View>
            ) : null}
          </Alert>
        ) : null}

        {devStatus?.services?.length ? (
          <Table marginTop="1rem" size="small" variation="striped">
            <TableHead>
              <TableRow>
                <TableCell as="th">Service</TableCell>
                <TableCell as="th">Desired</TableCell>
                <TableCell as="th">Running</TableCell>
                <TableCell as="th">Pending</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devStatus.services.map((s) => (
                <TableRow key={s.name}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.desired}</TableCell>
                  <TableCell>{s.running}</TableCell>
                  <TableCell>{s.pending}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </View>

      <View marginTop="2.5rem">
        <Heading level={4}>api3 health (proxy check)</Heading>
        <Text marginTop="0.5rem" color="font.secondary">
          Tests the <code>/ago</code> proxy route to api3 (M2M + tele secrets), not the
          direct browser → api.agorisk.com path used by Background Checks and User Management.
        </Text>

        <Flex marginTop="1.5rem" gap="1rem" alignItems="center">
          <Button variation="primary" onClick={runUpdateCheck} isLoading={loading}>
            Update check
          </Button>
          <Text fontSize="small" color="font.secondary">
            api_endpoint: <code>api3:health</code>
          </Text>
        </Flex>

        {error ? (
          <Alert variation="error" marginTop="1.5rem" isDismissible={false}>
            {error}
          </Alert>
        ) : null}

        {result ? (
          <View marginTop="1.5rem">
            <Text fontWeight="bold" marginBottom="0.5rem">
              Response
            </Text>
            <View
              as="pre"
              padding="1rem"
              backgroundColor="background.secondary"
              style={{
                overflow: 'auto',
                fontSize: '0.85rem',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(result, null, 2)}
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}
